import React, { Fragment, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './App.css';
import ProfileEditor from './components/ProfileEditor';
import ProfileView from './components/ProfileView';
import OpportunityEditor from './components/OpportunityEditor';
import MapComponent from './components/MapComponent';
import { KindeProvider, useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { getUserProfile, isProfileEmpty, getDomain } from './api/api';
import { Box, CircularProgress, AppBar, Container, Stack, Typography } from '@mui/material';
import Header from './components/Header';
import Body from './components/Body';
import Footer from './components/Footer';
import MessagesInbox from './components/MessagesInbox';
import MessageThread from './components/MessageThread';
import { UnreadMessagesProvider } from './contexts/UnreadMessagesContext';

// Restores the pre-login deep link after the Kinde redirect. Kinde returns to
// the origin root (the path/query can't be encoded in an allowlisted callback
// URL), so we carry the target through the OAuth `state` via login's app_state
// and have onRedirectCallback stash it here. Renders nothing; lives inside the
// router so it can navigate without a full page reload.
const PostLoginRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const target = sessionStorage.getItem('postLoginRedirect');
    if (!target) return;
    sessionStorage.removeItem('postLoginRedirect');
    if (target !== window.location.pathname + window.location.search) {
      navigate(target, { replace: true });
    }
  }, [navigate]);
  return null;
};

const App = () => {
  const { t } = useTranslation();
  const { user, login, logout, isLoading, isAuthenticated, getToken, register } = useKindeAuth();
  const [loginChanged, setLoginChanged] = useState(0);
  const [profile, setProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [domain, setDomain] = useState(null);
  const [domainVerified, setDomainVerified] = useState(false);
  
  const debug = window.location.hostname == "localhost" || window.location.hostname.startsWith('192');
  
  const urlParams = new URLSearchParams(window.location.search);
  const signUp = urlParams.has('signUp') || urlParams.has('signup');
  const subdomain = debug ? "kingston" : window.location.hostname.split('.')[0];
  console.log(subdomain);
    
  const onProfileLogout = async  ()=>{
    await logout();
//    setLoginChanged(loginChanged + 1);
//    window.location.href='/';
  }

  const checkUserProfile = async () => {
    try {
      const token = await getToken();
      const userProfile = await getUserProfile(token);
      setProfile(userProfile);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setProfile(null);
    } finally {
      setProfileLoaded(true);
    }
  };

  // Effect for map subdomain handling
  useEffect(() => {
    const isMapSubdomain = subdomain === 'app';
    if (!isMapSubdomain) return;

    // Check if URL contains noRedirect parameter
    const urlParams = new URLSearchParams(window.location.search);
    console.log("Single domain: " + window.SINGLE_DOMAIN);
    const noRedirect = window.SINGLE_DOMAIN ? false : urlParams.has('map');
    console.log("noRedirect: " + noRedirect);
    // Only redirect if noRedirect parameter is not present
    if (!noRedirect) {
      // Check localStorage for homeDomain before any login-related activities
      const homeDomain =  window.SINGLE_DOMAIN ?? localStorage.getItem("hd");  
      console.log("Home domain: " + homeDomain);
      if (homeDomain) {
        // Redirect to homeDomain.* + preserve the current path and query params
        
        if (window.SINGLE_DOMAIN) localStorage.setItem('hd', window.SINGLE_DOMAIN); 

        const currentPath = window.location.pathname;
        const currentSearch = window.location.search;
        const currentHash = window.location.hash;
        const baseDomain = window.location.host.split('.').slice(1).join('.');
        window.location.href = `${window.location.protocol}//${homeDomain}.${baseDomain}${currentPath}${currentSearch}${currentHash}`;            
        return;
      }
    }
    setDomainVerified(true);
  }, [subdomain]);

  // Effect for non-map subdomain handling and domain verification
  useEffect(() => {
    const isMapSubdomain = subdomain === 'app';
    if (isMapSubdomain) return;

    const verifyDomain = async () => {
      try {
        const domainInfo = await getDomain(subdomain);
        console.log(domainInfo);
        setDomain(domainInfo);
        setDomainVerified(true);
      } catch (error) {
        console.error('Invalid domain:', error);
        // When redirecting to app subdomain, preserve the current path and add map parameter
        const currentPath = window.location.pathname;
        const currentSearch = window.location.search ? window.location.search + '&map' : '?map';
        const currentHash = window.location.hash;
        const baseDomain = window.location.host.split('.').slice(1).join('.');
        window.location.href = `${window.location.protocol}//app.${baseDomain}${currentPath}${currentSearch}${currentHash}`;
      }
    };
    verifyDomain();
  }, [subdomain]);

  // Effect for authentication and profile loading
  useEffect(() => {
    // Only proceed if domain is verified and we're not on the map subdomain
    const isMapSubdomain = subdomain === 'app';
    if (!domainVerified || isMapSubdomain) return;

    // Handle authentication and profile loading
    if (!isLoading) {
      if (!isAuthenticated) {
        console.log("logging in");
        // Kinde's redirectUri is origin-only, so the deep-link path/query (e.g.
        // /profile?step=aboutYou from an email link) is lost across the auth
        // round-trip. Carry it through the OAuth state via app_state; it comes
        // back in onRedirectCallback (see AppWithProvider).
        const dest = window.location.pathname + window.location.search;
        const options = dest && dest !== '/' ? { app_state: { redirectTo: dest } } : undefined;
        if (signUp)
          register(options);
        else
          login(options);
      } else {
        console.log("loading profile " + user.id);
        checkUserProfile();
      }
    }
  }, [domainVerified, isLoading, isAuthenticated, loginChanged, subdomain, login, logout]);
  
  const isMapSubdomain = subdomain === 'app';
  if ((!domainVerified) || (!isMapSubdomain && (isLoading || !isAuthenticated || !profileLoaded))) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
   
      <div className="App">
         
        <BrowserRouter>

        <UnreadMessagesProvider enabled={Boolean(profile)}>
        <Fragment>
        <div className="row row-top">
           <Header profile={profile} city={domain?.name}/>
        </div>
        
       
            {isMapSubdomain ? (
           
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" sx={{ color: 'text.primary', textAlign: 'center', mt: 2, mb: 2 }}>
                    {t('map.findBranch')}
                  </Typography>
                  <Box sx={{ flex: 1, minHeight: 0, marginBottom: 3 }}>
                    <MapComponent />
                  </Box>
                </Box>
              ) : (
              <Fragment>
              <PostLoginRedirect />
              <Routes>
            <Route 
              path="/" 
              element={
                isProfileEmpty(profile) 
                  ? <Navigate to="/profile" replace /> 
                  : 
                  <Fragment>
                     <div className="grid-container">
                        <Body profile={profile} onProfileUpdated={checkUserProfile} domain={subdomain}/>
                     </div>
                     <div className="row row-bottom">
                      <AppBar
                          position="fixed"
                          color="transparent"
                          elevation={0}
                          sx={{ 
                            bottom: 0, 
                            top: 'auto', 
                            marginBottom: 0,
                            left: 'auto',
                            right: 'auto',
                            width: '100%',
                            maxWidth: '900px',
                            zIndex: 1000 /* Ensure footer stays above content */
                          }}>

                          <Footer /> 
                      </AppBar>
                      </div>
                  </Fragment>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <Box paddingLeft={5} paddingRight={5} paddingBottom={5} marginLeft={0} marginRight={0} marginBottom={0}>
                  <ProfileEditor 
                  
                    domain={subdomain}
                    onProfileLogout={onProfileLogout}
                    getToken={getToken} 
                    onProfileUpdated={checkUserProfile} 
                    user={user} 
                    profile={profile} 
                  />
                </Box>
              } 
            />
            <Route 
              path="/opportunity" 
              element={
                <Box paddingLeft={5} paddingRight={5} paddingBottom={5} marginLeft={0} marginRight={0} marginBottom={0}>
                  <OpportunityEditor 
                    domainInfo={domain}
                    getToken={getToken} 
                  />
                </Box>
              } 
            />
            <Route 
              path="/opportunity/:id" 
              element={
                <Box paddingLeft={5} paddingRight={5} paddingBottom={5} marginLeft={0} marginRight={0} marginBottom={0}>
                  <OpportunityEditor 
                    domainInfo={domain}
                    getToken={getToken} 
                  />
                </Box>
              } 
            />
            <Route
              path="/profile/:userId"
              element={
                <Box paddingLeft={5} paddingRight={5} paddingBottom={5} marginLeft={0} marginRight={0} marginBottom={0}>
                  <ProfileView />
                </Box>
              }
            />
            <Route
              path="/messages"
              element={
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                  <MessagesInbox />
                </Box>
              }
            />
            <Route
              path="/messages/:userId"
              element={
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <MessageThread />
                </Box>
              }
            />
              </Routes>
              </Fragment>
            )}




          </Fragment>
        </UnreadMessagesProvider>
        </BrowserRouter>
       
      </div>
   
  );
};

const AppWithProvider = () => {

  return (
  <KindeProvider
    clientId={window.REACT_APP_KINDE_CLIENT_ID ?? 'ff998a85685d469993ab264c42bd13ba'}
    domain={window.REACT_APP_KINDE_DOMAIN ?? 'https://auth.crewbank.ca'}
    redirectUri={ window.location.origin }
    logoutUri={window.REACT_APP_KINDE_LOGOUT_URI ?? 'http://localhost:3000'}
    onRedirectCallback={(user, appState) => {
      // Hand the deep-link target (set as app_state on login) to the in-router
      // PostLoginRedirect, which navigates to it once the app has authenticated.
      if (appState?.redirectTo) {
        sessionStorage.setItem('postLoginRedirect', appState.redirectTo);
      }
    }}
  >
    <App />
  </KindeProvider>
  );
};

export default AppWithProvider;
